import { useState, useRef, KeyboardEvent } from 'react'
import { X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface TagInputProps {
  value: string[]
  onChange: (tags: string[]) => void
  suggestions?: string[]
  maxTags?: number
  placeholder?: string
}

function sanitizeTag(tag: string): string {
  // Allow alphanumeric, hyphens, and spaces, trim whitespace
  return tag
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/gi, '')
}

function truncateTag(tag: string, maxLength: number = 30): string {
  return tag.length > maxLength ? `${tag.substring(0, maxLength)}...` : tag
}

export function TagInput({
  value,
  onChange,
  suggestions = [],
  maxTags = 10,
  placeholder = 'Add tags...',
}: TagInputProps) {
  const [inputValue, setInputValue] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const addTag = (tag: string) => {
    const sanitized = sanitizeTag(tag)
    if (
      sanitized &&
      !value.includes(sanitized) &&
      value.length < maxTags
    ) {
      onChange([...value, sanitized])
      setInputValue('')
      setShowSuggestions(false)
    }
  }

  const removeTag = (tagToRemove: string) => {
    onChange(value.filter((tag) => tag !== tagToRemove))
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addTag(inputValue)
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      // Remove last tag on backspace if input is empty
      const lastTag = value[value.length - 1]
      if (lastTag) {
        removeTag(lastTag)
      }
    }
  }

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue)
    setShowSuggestions(newValue.length > 0 && suggestions.length > 0)
  }

  const filteredSuggestions = suggestions
    .filter((suggestion) => {
      const sanitized = sanitizeTag(suggestion)
      const sanitizedInput = sanitizeTag(inputValue)
      return (
        sanitized.includes(sanitizedInput) &&
        !value.includes(sanitized)
      )
    })
    .slice(0, 5) // Limit to 5 suggestions

  const canAddMore = value.length < maxTags

  return (
    <div className="space-y-2">
      {/* Tags Display */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              className="gap-1 pr-1"
            >
              <span className="text-xs">{truncateTag(tag)}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-4 w-4 hover:bg-transparent"
                onClick={() => removeTag(tag)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(inputValue.length > 0 && suggestions.length > 0)}
          onBlur={() => {
            // Delay to allow clicking on suggestions
            setTimeout(() => setShowSuggestions(false), 200)
          }}
          placeholder={canAddMore ? placeholder : `Maximum ${maxTags} tags reached`}
          disabled={!canAddMore}
          className="text-sm"
        />

        {/* Suggestions Dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-lg shadow-sm">
            {filteredSuggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => addTag(suggestion)}
                className={cn(
                  'w-full px-3 py-2 text-left text-sm hover:bg-muted',
                  'first:rounded-t-lg last:rounded-b-lg'
                )}
              >
                {truncateTag(suggestion)}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Helper Text */}
      <p className="text-meta">
        Press Enter or comma to add. {value.length}/{maxTags} tags used.
      </p>
    </div>
  )
}
